import { Badge, Button, Table } from 'react-bootstrap';
import './MercurXLaunchpadUserTable.scss';
import { connect } from 'react-redux';
import Swal from 'sweetalert2';
import { mainColors } from '../../helpers/colors';
import useMercurxPagination from '../../hooks/useMercurxPagination';
import MercurXTablePagination from '../MercurXTablePagination/MercurXTablePagination';
import MercurXSortableHeader from '../MercurXSortableHeader/MercurXSortableHeader';
import {
  resendLaunchpadActivationEmailActionRequest,
  setLaunchpadUsersSortDataAction,
  sortLaunchpadUsersAction,
  updateLaunchpadUserActionRequest,
} from '../../store/launchpadUser/launchpadUserActions';
import {
  LAUNCHPADUSERS_SORT_KEYS,
  LAUNCHPADUSERS_SORT_TYPES,
} from '../../pages/LaunchpadUsers/launchpadUsersConstants';

function MercurXLaunchpadUserTable(props) {
  const {
    launchpadUsers,
    filteredLaunchpadUsers,
    launchpadUsersSortData,
    setLaunchpadUsersSortData,
    sortLaunchpadUsers,
    updateLaunchpadUserRequest,
    resendLaunchpadActivationEmailRequest,
  } = props;
  const sourceData = filteredLaunchpadUsers || launchpadUsers || [];
  const user = JSON.parse(localStorage.getItem('user'))?.user;
  const {
    currentPage,
    totalPages,
    visiblePages,
    pagedData: renderedLaunchpadUsers,
    rowOffset,
    isFirstPage,
    isLastPage,
    goToPage,
    goToFirstPage,
    goToPrevPage,
    goToNextPage,
    goToLastPage,
  } = useMercurxPagination(sourceData, 10, 5);

  function scrollToLaunchpadUsersContainer() {
    const launchpadUsersContainer = document.getElementById('launchpadUsers-container');
    if (launchpadUsersContainer) {
      launchpadUsersContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const handlePageChange = (page) => {
    goToPage(page);
    scrollToLaunchpadUsersContainer();
  };

  const handleFirstPage = () => {
    goToFirstPage();
    scrollToLaunchpadUsersContainer();
  };

  const handlePrevPage = () => {
    goToPrevPage();
    scrollToLaunchpadUsersContainer();
  };

  const handleNextPage = () => {
    goToNextPage();
    scrollToLaunchpadUsersContainer();
  };

  const handleLastPage = () => {
    goToLastPage();
    scrollToLaunchpadUsersContainer();
  };

  const isUserActive = (launchpadUser) =>
    launchpadUser?.is_active?.toString().toLowerCase() === 'true';

  const formatLastActiveAt = (lastActiveAt) => {
    if (!lastActiveAt) {
      return 'No activity yet';
    }

    const parsedDate = new Date(lastActiveAt);
    if (Number.isNaN(parsedDate.getTime())) {
      return 'No activity yet';
    }

    const dateParts = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(parsedDate);

    const datePartMap = dateParts.reduce((accumulator, part) => {
      accumulator[part.type] = part.value;
      return accumulator;
    }, {});

    const formattedDate = `${datePartMap.month}/${datePartMap.day}/${datePartMap.year}`;
    const formattedTime = `${datePartMap.hour}:${datePartMap.minute}`;

    return `${formattedDate} ${formattedTime}`;
  };

  const handleToggleActivation = (launchpadUser) => {
    const activeNow = isUserActive(launchpadUser);
    const nextStatus = activeNow ? 'False' : 'True';
    const actionLabel = activeNow ? 'deactivate' : 'activate';

    Swal.fire({
      icon: 'question',
      title: `${activeNow ? 'Deactivate' : 'Activate'} User?`,
      text: `Do you want to ${actionLabel} ${launchpadUser.full_name}?`,
      showCancelButton: true,
      confirmButtonColor: mainColors.primary,
      cancelButtonColor: '#6c757d',
      confirmButtonText: activeNow ? 'Deactivate' : 'Activate',
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      updateLaunchpadUserRequest({
        userID: launchpadUser.id,
        body: {
          is_active: nextStatus,
        },
      });
    });
  };

  const handleResendActivationEmail = (launchpadUser) => {
    Swal.fire({
      icon: 'question',
      title: 'Resend Activation Email?',
      text: `A new activation email will be sent to ${launchpadUser.email}.`,
      showCancelButton: true,
      confirmButtonColor: mainColors.primary,
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Resend Email',
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      resendLaunchpadActivationEmailRequest({
        userID: launchpadUser.id,
      });
      });
  };

  const handleSort = (sortKey, defaultSortType) => {
    const nextSortType = launchpadUsersSortData.sortKey === sortKey
      ? (launchpadUsersSortData.sortType === LAUNCHPADUSERS_SORT_TYPES.ASCENDING
        ? LAUNCHPADUSERS_SORT_TYPES.DESCENDING
        : LAUNCHPADUSERS_SORT_TYPES.ASCENDING)
      : defaultSortType;

    setLaunchpadUsersSortData({
      sortKey,
      sortType: nextSortType,
    });
    sortLaunchpadUsers();
  };

  return (
    <>
      <Table className="launchpadUsers-table-design" responsive hover borderless>
        <thead>
          <tr className="text-t-body-color text-fs-tag">
            <th>#</th>
            <th>
              <MercurXSortableHeader
                label="Full Name"
                isActive={launchpadUsersSortData.sortKey === LAUNCHPADUSERS_SORT_KEYS.FULL_NAME}
                isAscending={
                  launchpadUsersSortData.sortType === LAUNCHPADUSERS_SORT_TYPES.ASCENDING
                }
                onClick={() => handleSort(
                  LAUNCHPADUSERS_SORT_KEYS.FULL_NAME,
                  LAUNCHPADUSERS_SORT_TYPES.ASCENDING
                )}
              />
            </th>
            <th>
              <MercurXSortableHeader
                label="Email"
                isActive={launchpadUsersSortData.sortKey === LAUNCHPADUSERS_SORT_KEYS.EMAIL}
                isAscending={
                  launchpadUsersSortData.sortType === LAUNCHPADUSERS_SORT_TYPES.ASCENDING
                }
                onClick={() => handleSort(
                  LAUNCHPADUSERS_SORT_KEYS.EMAIL,
                  LAUNCHPADUSERS_SORT_TYPES.ASCENDING
                )}
              />
            </th>
            <th>
              <MercurXSortableHeader
                label="Kyc Status"
                isActive={launchpadUsersSortData.sortKey === LAUNCHPADUSERS_SORT_KEYS.KYC_STATUS}
                isAscending={
                  launchpadUsersSortData.sortType === LAUNCHPADUSERS_SORT_TYPES.ASCENDING
                }
                onClick={() => handleSort(
                  LAUNCHPADUSERS_SORT_KEYS.KYC_STATUS,
                  LAUNCHPADUSERS_SORT_TYPES.DESCENDING
                )}
              />
            </th>
            <th>
              <MercurXSortableHeader
                label="2FA Status"
                isActive={
                  launchpadUsersSortData.sortKey === LAUNCHPADUSERS_SORT_KEYS.TWO_FACTOR_STATUS
                }
                isAscending={
                  launchpadUsersSortData.sortType === LAUNCHPADUSERS_SORT_TYPES.ASCENDING
                }
                onClick={() => handleSort(
                  LAUNCHPADUSERS_SORT_KEYS.TWO_FACTOR_STATUS,
                  LAUNCHPADUSERS_SORT_TYPES.DESCENDING
                )}
              />
            </th>
            <th>
              <MercurXSortableHeader
                label="Last Active"
                isActive={launchpadUsersSortData.sortKey === LAUNCHPADUSERS_SORT_KEYS.LAST_ACTIVE}
                isAscending={
                  launchpadUsersSortData.sortType === LAUNCHPADUSERS_SORT_TYPES.ASCENDING
                }
                onClick={() => handleSort(
                  LAUNCHPADUSERS_SORT_KEYS.LAST_ACTIVE,
                  LAUNCHPADUSERS_SORT_TYPES.DESCENDING
                )}
              />
            </th>
            <th>
              <MercurXSortableHeader
                label="Status"
                isActive={launchpadUsersSortData.sortKey === LAUNCHPADUSERS_SORT_KEYS.STATUS}
                isAscending={
                  launchpadUsersSortData.sortType === LAUNCHPADUSERS_SORT_TYPES.ASCENDING
                }
                onClick={() => handleSort(
                  LAUNCHPADUSERS_SORT_KEYS.STATUS,
                  LAUNCHPADUSERS_SORT_TYPES.DESCENDING
                )}
              />
            </th>
            {user?.is_superuser && <th>Actions</th>}
          </tr>
        </thead>
        {renderedLaunchpadUsers?.length ? (
          <tbody>
            {renderedLaunchpadUsers.map((item, index) => {
              const kycStatus = Array.isArray(item.kyc) ? item.kyc[0]?.kyc_status : null;
              const active = isUserActive(item);
              const formattedLastActive = formatLastActiveAt(item.last_active_at);

              return (
                <tr className="text-t-head-color" key={item.id || index}>
                  <td>{rowOffset + index + 1}</td>
                  <td>{item.full_name}</td>
                  <td>{item.email}</td>
                  <td>
                    {!kycStatus ||
                    kycStatus === 'false' ||
                    kycStatus === 'Cancelled' ? (
                      <Badge bg="danger">Rejected</Badge>
                    ) : kycStatus === 'Approved' ? (
                      <Badge bg="success">Approved</Badge>
                    ) : (
                      <Badge bg="warning">In Progress</Badge>
                    )}
                  </td>
                  <td>
                    {item.two_factor_auth ? (
                      <Badge bg="success">Enabled</Badge>
                    ) : (
                      <Badge bg="secondary">Disabled</Badge>
                    )}
                  </td>
                  <td>
                    <span
                      className={
                        item.last_active_at ? 'user-last-active' : 'user-last-active-empty'
                      }
                    >
                      {formattedLastActive}
                    </span>
                  </td>
                  <td>
                    {active ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="danger">Inactive</Badge>
                    )}
                  </td>
                  {user?.is_superuser && (
                    <td>
                      <div className="user-table-actions">
                        <Button
                          size="sm"
                          variant={active ? 'outline-danger' : 'outline-success'}
                          className="user-action-button"
                          onClick={() => handleToggleActivation(item)}
                        >
                          {active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="user-action-button"
                          onClick={() => handleResendActivationEmail(item)}
                          disabled={active}
                          title={active ? 'Already active' : 'Resend activation email'}
                        >
                          Resend Mail
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        ) : null}
      </Table>
      {!renderedLaunchpadUsers?.length && (
        <div className="d-flex text-fs-body-md text-t-body-color justify-content-center">
          No launchpad user found according to search results.
        </div>
      )}
      <MercurXTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        visiblePages={visiblePages}
        isFirstPage={isFirstPage}
        isLastPage={isLastPage}
        onFirstPage={handleFirstPage}
        onPrevPage={handlePrevPage}
        onPageChange={handlePageChange}
        onNextPage={handleNextPage}
        onLastPage={handleLastPage}
      />
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    launchpadUsers: state.launchpadUserReducer.launchpadUsers,
    filteredLaunchpadUsers: state.launchpadUserReducer.filteredLaunchpadUsers,
    launchpadUsersSortData: state.launchpadUserReducer.launchpadUsersSortData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setLaunchpadUsersSortData: (payload) => {
      dispatch(setLaunchpadUsersSortDataAction(payload));
    },
    sortLaunchpadUsers: () => {
      dispatch(sortLaunchpadUsersAction());
    },
    updateLaunchpadUserRequest: (payload) => {
      dispatch(updateLaunchpadUserActionRequest(payload));
    },
    resendLaunchpadActivationEmailRequest: (payload) => {
      dispatch(resendLaunchpadActivationEmailActionRequest(payload));
    },
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(MercurXLaunchpadUserTable);
